/**
 * ==================== 视频图层模块 - 图层工厂 ====================
 *
 * 职责：视频图层配置构建和实例创建，连接数据层到 Canvas
 *
 * 导出：
 * - createVideoLayerConfig() — 参数 → 图层配置对象
 * - createVideoLayer() — 异步创建视频图层并添加到画布
 * - isVideoLayer() — 类型守卫函数
 *
 * 依赖：./FabricVideo（ensureFabricVideoRegistered）、./types（VideoLayerMetaV1）
 * 被依赖：task.ts、index.ts
 */

import { fabric } from 'fabric'
import { useCanvasEditor } from '../../hooks/useCanvasEditor'
import * as layerUtils from '../layerUtils'
import { CANVAS_OBJECT_CONTROL_CONFIG } from '../../../helper/canvasConfig'
import { ensureFabricVideoRegistered } from './FabricVideo'
import { resolveVideoFabricScale } from './constants'
import type { VideoLayerMetaV1 } from './types'

/**
 * 构建视频图层的配置对象（extraData 序列化为 JSON）
 *
 * @param options - 视频元数据
 * @returns 图层配置对象，包含 type、name、src、extraData 等字段
 */
export const createVideoLayerConfig = (options: {
  videoUrl: string
  coverUrl: string
  duration: number
  title: string
}): Record<string, any> => {
  const meta: VideoLayerMetaV1 = {
    v: 1,
    videoUrl: options.videoUrl,
    coverUrl: options.coverUrl,
    duration: options.duration,
    title: options.title,
  }

  return {
    type: 'video',
    name: options.title,
    src: options.coverUrl,
    originalSrc: options.coverUrl,
    extraData: JSON.stringify(meta),
  }
}

/**
 * 异步创建视频图层并添加到画布
 *
 * 流程：
 * 1. 注册 FabricVideo 类到 fabric 命名空间
 * 2. 加载封面图（支持预加载的 HTMLImageElement 或自动加载）
 * 3. 构建 fabric 选项并实例化 FabricVideo
 * 4. 添加到画布并触发渲染
 *
 * @param layerProps - 图层属性（需包含 src 和 extraData）
 * @param options - 可选配置（autoSelect、preloadedImage、title）
 * @returns Promise<fabric.Image> 创建的图层实例
 */
export const createVideoLayer = (
  layerProps: Record<string, any> & {
    src: string
    extraData: string
    name?: string
  },
  options?: {
    autoSelect?: boolean
    preloadedImage?: HTMLImageElement
    title?: string
  },
) => {
  ensureFabricVideoRegistered()
  const { getEditor } = useCanvasEditor()
  const { autoSelect = true, preloadedImage, title } = options || {}

  return new Promise<fabric.Image>(async (resolve, reject) => {
    // 使用外部传入的 name 或 title，如果没有则使用默认名
    const finalTitle =
      layerProps.name || title || layerUtils.generateLayerName('video')

    const layerData = layerUtils.generateLayer('image', {
      ...layerProps,
      name: finalTitle,
    } as any) as any
    layerData.type = 'video'

    if (!layerData.src) {
      reject(new Error('创建视频封面 src 不能为空'))
      return
    }

    let imgElem: HTMLImageElement
    if (preloadedImage) {
      imgElem = preloadedImage
    } else {
      imgElem = new Image()
      imgElem.crossOrigin = 'anonymous'
      imgElem.src = layerData.src
      try {
        await imgElem.decode()
      } catch {
        if (!imgElem.complete || imgElem.naturalWidth === 0) {
          await new Promise((res, rej) => {
            imgElem.onload = res
            imgElem.onerror = rej
          }).catch((e) => {
            reject(new Error(`图片加载失败: ${e}`))
            return
          })
        }
      }
    }

    const editor = getEditor()
    const canvas = editor?.fabricCanvas
    if (!canvas) {
      reject(new Error('画布未初始化'))
      return
    }

    const hasUserSpecifiedWidth = layerProps.width !== undefined
    const hasUserSpecifiedHeight = layerProps.height !== undefined
    // 调用方传入的 width/height 视为目标显示尺寸；Fabric 基线保持封面原始像素，用 scale 控制舞台大小
    const fabricScale =
      hasUserSpecifiedWidth || hasUserSpecifiedHeight
        ? resolveVideoFabricScale(
            imgElem.naturalWidth,
            imgElem.naturalHeight,
            hasUserSpecifiedWidth ? layerData.width : undefined,
            hasUserSpecifiedHeight ? layerData.height : undefined,
          )
        : null

    const fabricOptions: Record<string, any> = {
      crossOrigin: 'anonymous',
      ...CANVAS_OBJECT_CONTROL_CONFIG.normal,
    }

    Object.keys(layerData).forEach((key) => {
      if (key !== 'width' && key !== 'height' && key !== 'scaleX' && key !== 'scaleY') {
        fabricOptions[key] = (layerData as any)[key]
      }
    })

    if (fabricScale) {
      fabricOptions.width = fabricScale.width
      fabricOptions.height = fabricScale.height
      fabricOptions.scaleX = fabricScale.scaleX
      fabricOptions.scaleY = fabricScale.scaleY
    }

    // 注入原始尺寸元数据（从封面图获取，用于信息标签显示等场景）
    fabricOptions.originalWidth = imgElem.naturalWidth
    fabricOptions.originalHeight = imgElem.naturalHeight

    const VideoKlass = (fabric as any).Video as typeof fabric.Image
    const videoObj = new (VideoKlass as any)(
      imgElem,
      fabricOptions,
    ) as fabric.Image
    ;(videoObj as any).type = 'video'
    ;(videoObj as any).extraData = layerData.extraData

    canvas.add(videoObj)
    if (autoSelect) {
      canvas.setActiveObject(videoObj)
    }
    canvas.requestRenderAll()
    resolve(videoObj)
  })
}

/**
 * 判断一个 fabric.Object 是否为视频图层（类型守卫）
 */
export const isVideoLayer = (
  obj: fabric.Object | undefined | null,
): boolean => {
  return (obj as any)?.type === 'video'
}
